import HyperDX from "@hyperdx/browser";

let isHyperDXInitialized = false;

type HyperDXPrimitive = string | number | boolean;
type HyperDXAttributes = Record<string, HyperDXPrimitive>;

function normalizeHyperDXError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error("Unknown application error");
}

function isRecordLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function appendAttribute(
  target: HyperDXAttributes,
  key: string,
  value: unknown,
): void {
  if (value === undefined || value === null) {
    return;
  }

  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    target[key] = value;
    return;
  }

  if (value instanceof Error) {
    target[`${key}.name`] = value.name;
    target[`${key}.message`] = value.message;
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendAttribute(target, `${key}.${index}`, item);
    });
    return;
  }

  if (isRecordLike(value)) {
    Object.entries(value).forEach(([childKey, childValue]) => {
      appendAttribute(target, `${key}.${childKey}`, childValue);
    });
  }
}

function sanitizeHyperDXAttributes(
  attributes: Record<string, unknown>,
): HyperDXAttributes {
  const sanitized: HyperDXAttributes = {};

  Object.entries(attributes).forEach(([key, value]) => {
    appendAttribute(sanitized, key, value);
  });

  return sanitized;
}

function buildTracePropagationTargets(): (string | RegExp)[] {
  const targets: (string | RegExp)[] = [];

  if (import.meta.env.DEV) {
    targets.push(/^\/api\//);
  }

  const apiBases = [
    import.meta.env.VITE_API_BASE_URL,
    import.meta.env.VITE_API_BASE_URL_UNIV,
  ].filter(Boolean);

  for (const base of apiBases) {
    try {
      const origin = new URL(base).origin;
      if (!targets.includes(origin)) {
        targets.push(origin);
      }
    } catch {
      // Ignore invalid URLs and keep initializing the SDK.
    }
  }

  return targets;
}

export function initHyperDX() {
  const apiKey = import.meta.env.VITE_HYPERDX_API_KEY;
  const collectorUrl = import.meta.env.VITE_HYPERDX_URL;
  if (!apiKey) {
    return;
  }

  HyperDX.init({
    apiKey,
    service: "career-counseling-front",
    ...(collectorUrl ? { url: collectorUrl } : {}),
    tracePropagationTargets: buildTracePropagationTargets(),
    // Console logs are captured only in HyperDX to avoid duplicate issues in Sentry.
    consoleCapture: true,
    advancedNetworkCapture: true, // FIXME: 운영 서버에서는 제외.
    otelResourceAttributes: {
      "deployment.environment": import.meta.env.MODE,
      "service.version": import.meta.env.VITE_APP_RELEASE,
    },
  });

  isHyperDXInitialized = true;
}

export function recordHyperDXAction(
  name: string,
  attributes: Record<string, unknown>,
) {
  if (!isHyperDXInitialized) {
    return;
  }

  HyperDX.addAction(name, sanitizeHyperDXAttributes(attributes));
}

export function recordHyperDXException(
  error: unknown,
  attributes?: Record<string, unknown>,
) {
  if (!isHyperDXInitialized) {
    return;
  }

  const normalizedError = normalizeHyperDXError(error);
  const sanitizedAttributes = sanitizeHyperDXAttributes({
    error_name: normalizedError.name,
    error_message: normalizedError.message,
    ...attributes,
  });

  HyperDX.recordException(normalizedError, sanitizedAttributes);
}
